/**
 * The audit queue (#1292) — a walkthrough grid.
 *
 * Converged 2026-09-03 from two of six sketches: the dense sortable grid and
 * the one-at-a-time deck. They are opposite answers to the same question —
 * all overview versus all focus — and the fusion rule is one sentence:
 * **the overview owns navigation, the focus owns the decision.** The grid
 * stays dense and sortable; exactly one row at a time is open as a full card
 * with everything the decision needs; J/K walk, Enter accepts, M moves.
 *
 * The walk order IS the current sort. That is the quiet win over both
 * parents: sort by Waiting and the walkthrough becomes "clear the oldest
 * first" — ordering stops being a way of looking and becomes the working
 * strategy.
 *
 * ⚠️ It still lists **everything**, not a flagged subset: the expensive case
 * is the classifier and the submitter agreeing, confidently, and both being
 * wrong. That raises no flag, so only a queue that shows everything can
 * surface it.
 *
 * ⚠️ The default order comes from the server (`sort=review`, in SQL, so it
 * holds across pages). A column click re-orders **the loaded page** client-
 * side — an explicit request, not a silent repair, and page_size 200 covers
 * today's New stage many times over.
 *
 * "Looks right ✓" PERSISTS (owner call, 2026-09-10 — it was session ticks
 * before, and the queue asked the next visitor the same question about a
 * demand somebody had already vouched for). The vouch is a row on the
 * demand (`landing_confirmed_at/by`), it sinks the row into the Confirmed
 * tier, and routing the demand clears it — a vouch describes the landing
 * it was given on. The local `reviewed` set survives only as this
 * sitting's walk bookkeeping (what the cursor should skip).
 */

import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  useRestoreFocusTarget,
} from '@fluentui/react-components'
import { useConfirmLanding, useDemands } from '../hooks/useDemands'
import { useToast } from '../components/ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { activatable } from '../lib/activatable'
import {
  REVIEW_TIER_ORDER,
  type ReviewTier,
  reviewTier,
  reviewTierLabel,
} from '../lib/reviewTiers'
import {
  RouteDemandDialog,
  DIGITAL_COUNCIL_TARGET,
  landingCodeLabel,
} from '../components/RouteDemandDialog'
import type { Demand } from '../types/demand'

const useStyles = makeStyles({
  page: {
    padding: tokens.spacingVerticalXL,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '1180px',
  },
  head: { marginBottom: tokens.spacingVerticalL },
  lede: {
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
    maxWidth: '68ch',
  },
  card: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    overflowX: 'auto',
  },
  progress: {
    display: 'flex',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  progressCount: { fontVariantNumeric: 'tabular-nums', color: tokens.colorNeutralForeground2 },

  table: { tableLayout: 'fixed', minWidth: '900px' },
  /* All percentages: with table-layout fixed, any leftover width is dumped
     entirely onto a lone pixel-width column, which is how a 34px flag column
     rendered 170px wide. Percentages scale together instead. */
  colFlag: { width: '4%' },
  colTitle: { width: '44%' },
  colCode: { width: '11%' },
  colConf: { width: '15%' },
  colWait: { width: '9%' },

  sortable: { cursor: 'pointer', userSelect: 'none' },

  row: { cursor: 'pointer' },
  rowIdle: { ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover } },
  rowCursor: { backgroundColor: tokens.colorBrandBackground2 },
  // A handled row recedes with a tint, not by fading its text: opacity took
  // the titles below AA contrast (#1385), and a handled row is not disabled.
  rowDone: { backgroundColor: tokens.colorNeutralBackground2 },
  cellTop: { verticalAlign: 'top' },

  dot: { display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%' },
  dotOverridden: { backgroundColor: tokens.colorPaletteRedBorderActive },
  dotUntrusted: { backgroundColor: tokens.colorPaletteDarkOrangeBorderActive },
  dotUnclassified: { backgroundColor: tokens.colorPaletteYellowBorderActive },
  dotSettled: { backgroundColor: tokens.colorNeutralStroke1 },
  dotRouted: { backgroundColor: tokens.colorNeutralForeground4 },
  tick: { color: tokens.colorPaletteGreenForeground1, fontWeight: tokens.fontWeightBold },

  title: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  /* The title is a real link into the demand — the row's most natural click.
     Everything else on the row moves the cursor instead, so walking and
     opening stop competing for the same gesture. A real <a> also gives
     middle-click and ctrl+click for free. */
  titleLink: { color: tokens.colorBrandForeground1, textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' } },
  sub: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: '2px',
  },
  code: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusSmall,
    ...shorthands.padding('1px', tokens.spacingHorizontalXS),
    whiteSpace: 'nowrap',
  },
  codeUn: {
    ...shorthands.borderColor(tokens.colorPaletteYellowBorderActive),
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  codeDc: { color: tokens.colorNeutralForeground3 },
  codeOverridden: {
    ...shorthands.borderColor(tokens.colorPaletteRedBorderActive),
    color: tokens.colorPaletteRedForeground1,
  },
  wasFa: { color: tokens.colorNeutralForeground4, fontSize: tokens.fontSizeBase100 },

  conf: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalXS },
  confNum: { fontVariantNumeric: 'tabular-nums', minWidth: '2.6em' },
  confTrack: {
    width: '46px',
    height: '4px',
    borderRadius: '2px',
    backgroundColor: tokens.colorNeutralBackground5,
    overflowX: 'hidden',
  },
  confFill: { height: '100%', backgroundColor: tokens.colorBrandBackground },
  confFillWeak: { backgroundColor: tokens.colorPaletteDarkOrangeBorderActive },
  none: { color: tokens.colorNeutralForeground4 },
  num: { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  stale: { color: tokens.colorPaletteDarkOrangeForeground1, fontWeight: tokens.fontWeightSemibold },

  /* The open card: the deck half. One row at a time, everything the decision
     needs, nothing to open in another tab. */
  openCell: {
    backgroundColor: tokens.colorBrandBackground2,
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorBrandStroke1,
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalXL),
  },
  openTitle: {
    marginTop: '2px',
    marginBottom: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  openDesc: {
    color: tokens.colorNeutralForeground2,
    maxWidth: '72ch',
    marginTop: tokens.spacingVerticalS,
  },
  verdict: {
    marginTop: tokens.spacingVerticalM,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorBrandStroke1,
    borderRadius: `0 ${tokens.borderRadiusSmall} ${tokens.borderRadiusSmall} 0`,
    maxWidth: '72ch',
    fontSize: tokens.fontSizeBase300,
  },
  verdictHot: { borderLeftColor: tokens.colorPaletteRedBorderActive },
  acts: {
    display: 'flex',
    columnGap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  kbd: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase100,
    marginTop: tokens.spacingVerticalS,
  },
  openLink: { color: tokens.colorBrandForeground1, alignSelf: 'center' },

  empty: {
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    textAlign: 'center',
  },
})

/** Past this, a demand has been waiting long enough to call out on its own. */
const STALE_AFTER_DAYS = 14

type SortKey = 'tier' | 'landed' | 'confidence' | 'waiting'

/** Whole days a demand has been waiting. `formatRelative` is deliberately not
 *  used: it falls back to a locale date past 7 days, so the rows that have
 *  waited longest — the whole point of the column — stop showing a duration. */
function daysWaiting(iso?: string | null): number | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000))
}

/** Where a demand is right now: its focus area, or the Digital Council. */
function location(demand: Demand): string {
  return demand.disposition === 'digital_council' ? DIGITAL_COUNCIL_TARGET : demand.focus_area_code
}

// Anything that answers Enter on its own. Letters (J/K/M) never collide with
// these, so only Enter consults this list.
const INTERACTIVE =
  'button, a[href], input, textarea, select, summary, [contenteditable="true"], ' +
  '[role="button"], [role="link"], [role="menuitem"], [role="option"], [role="tab"], ' +
  '[role="switch"], [role="checkbox"], [role="radio"], [tabindex]:not([tabindex="-1"])'

function ownsEnter(target: EventTarget | null): boolean {
  return target instanceof Element && target !== document.body && !!target.closest(INTERACTIVE)
}

export function AuditQueue() {
  const styles = useStyles()
  // Marks the buttons that open the Move dialog as where focus goes back to
  // when it closes. The dialog is state-driven, so Fluent cannot know its
  // trigger; without this, focus fell to the notifications bell (#1385).
  const restoreFocusTarget = useRestoreFocusTarget()
  const [routing, setRouting] = useState<Demand | null>(null)
  const [reviewed, setReviewed] = useState<Set<number>>(() => new Set())
  const [cursorId, setCursorId] = useState<number | null>(null)
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortAsc, setSortAsc] = useState(true)

  const query = useDemands({ stage: 'new', sort: 'review', page_size: 200, allPages: true })
  const confirmLanding = useConfirmLanding()
  const { showToast } = useToast()
  const items = useMemo(() => query.data?.items ?? [], [query.data])

  const rows = useMemo(() => {
    if (!sortKey) return items // the server's review order, untouched
    return [...items].sort((a, b) => {
      let r = 0
      if (sortKey === 'tier')
        r = REVIEW_TIER_ORDER.indexOf(reviewTier(a)) - REVIEW_TIER_ORDER.indexOf(reviewTier(b))
      if (sortKey === 'landed') r = location(a).localeCompare(location(b))
      if (sortKey === 'confidence')
        r = (a.suggestion_confidence ?? -1) - (b.suggestion_confidence ?? -1)
      if (sortKey === 'waiting')
        r =
          (daysWaiting(a.submission_date ?? a.created_at) ?? 0) -
          (daysWaiting(b.submission_date ?? b.created_at) ?? 0)
      return sortAsc ? r : -r
    })
  }, [items, sortKey, sortAsc])

  // The cursor: exactly one row is open. Defaults to the first row, and heals
  // itself if the row it pointed at left the page.
  const cursor = useMemo(() => {
    const found = cursorId != null ? rows.find((d) => d.id === cursorId) : undefined
    return found ?? rows[0] ?? null
  }, [rows, cursorId])

  /** Advance to the next unreviewed row after `from`, wrapping. */
  function advance(from: Demand, justReviewed: Set<number>) {
    const start = rows.findIndex((d) => d.id === from.id)
    for (let step = 1; step <= rows.length; step++) {
      const next = rows[(start + step) % rows.length]
      if (next && !justReviewed.has(next.id)) {
        setCursorId(next.id)
        return
      }
    }
  }

  function passCursor() {
    if (!cursor || confirmLanding.isPending) return
    // A handled row is done — Enter on it just walks on, never re-confirms.
    if (reviewed.has(cursor.id) || cursor.landing_confirmed_at || cursor.disposition) {
      advance(cursor, reviewed)
      return
    }
    const accepted = cursor
    confirmLanding.mutate(accepted.id, {
      onSuccess: () => {
        const next = new Set(reviewed)
        next.add(accepted.id)
        setReviewed((previous) => new Set(previous).add(accepted.id))
        advance(accepted, next)
      },
      onError: (err) =>
        showToast({
          title: 'Could not save the confirmation',
          body: apiErrorMessage(err),
          intent: 'error',
        }),
    })
  }

  /** Ticked this sitting, confirmed durably by anyone, or routed onward. */
  function isHandled(demand: Demand): boolean {
    return reviewed.has(demand.id) || !!demand.landing_confirmed_at || !!demand.disposition
  }

  function openMove(demand: Demand) {
    setRouting(demand)
  }

  // J/K walk, Enter passes, M moves — unless a dialog is open or the user is
  // typing somewhere.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (routing) return
      const t = e.target as HTMLElement | null
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return
      // Keys pressed inside a menu, listbox or another dialog are that
      // surface's business, not the queue's.
      if (t instanceof Element && t.closest('[role="dialog"], [role="menu"], [role="listbox"]')) return
      if (!cursor || rows.length === 0) return
      const idx = rows.findIndex((d) => d.id === cursor.id)
      if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowDown') {
        setCursorId(rows[Math.min(idx + 1, rows.length - 1)]?.id ?? null)
        e.preventDefault()
      } else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowUp') {
        setCursorId(rows[Math.max(idx - 1, 0)]?.id ?? null)
        e.preventDefault()
      } else if (e.key === 'Enter') {
        // Enter belongs to whatever has focus. A focused button, link or row
        // answers Enter itself, and taking it here as well is how Enter on
        // "Move…" accepted the row instead of opening the dialog (#1385).
        // The one focused thing whose Enter IS "accept" is the selected row.
        const onSelectedRow =
          t instanceof Element && t.getAttribute('data-audit-row') === String(cursor.id)
        if (!onSelectedRow && ownsEnter(e.target)) return
        passCursor()
      } else if (e.key === 'm' || e.key === 'M') {
        openMove(cursor)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  function sortBy(key: SortKey) {
    if (sortKey === key) setSortAsc((v) => !v)
    else {
      setSortKey(key)
      setSortAsc(true)
    }
  }
  const arrow = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '')

  const dotFor: Record<ReviewTier, string> = {
    overridden: styles.dotOverridden,
    untrusted: styles.dotUntrusted,
    unclassified: styles.dotUnclassified,
    settled: styles.dotSettled,
    // Same quiet treatment as settled — a confirmed row usually shows the
    // ✓ tick instead of a dot; the dot appears only pre-refetch.
    confirmed: styles.dotSettled,
    routed: styles.dotRouted,
  }

  if (query.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner label="Loading the queue…" />
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className={styles.page}>
        <MessageBar intent="error">
          <MessageBarBody>
            Failed to load the audit queue. {apiErrorMessage(query.error)}
          </MessageBarBody>
        </MessageBar>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <Title2>Audit queue</Title2>
        <div className={styles.lede}>Every demand still in New, most suspect first.</div>
      </div>

      {rows.length === 0 ? (
        <div className={styles.empty} data-testid="audit-queue-empty">
          Nothing is waiting in New.
        </div>
      ) : (
        <div className={styles.card}>
          <div className={styles.progress} data-testid="audit-progress">
            <span>J/K move · Enter accept · M move</span>
            {/* Handled rows, not this visit's ticks: a confirmation is durable
                and a routed row is done, so "0 / 4" over three ✓ rows was the
                counter contradicting the table under it (#1375). */}
            <span className={styles.progressCount}>
              {rows.filter(isHandled).length} / {rows.length} handled
            </span>
          </div>
          <Table size="small" aria-label="Audit queue" className={styles.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  className={mergeClasses(styles.colFlag, styles.sortable)}
                  onClick={() => sortBy('tier')}
                >
                  ⚑{arrow('tier')}
                </TableHeaderCell>
                <TableHeaderCell className={styles.colTitle}>Demand</TableHeaderCell>
                <TableHeaderCell
                  className={mergeClasses(styles.colCode, styles.sortable)}
                  onClick={() => sortBy('landed')}
                >
                  Landed in{arrow('landed')}
                </TableHeaderCell>
                <TableHeaderCell className={styles.colCode}>Suggested</TableHeaderCell>
                <TableHeaderCell
                  className={mergeClasses(styles.colConf, styles.sortable)}
                  onClick={() => sortBy('confidence')}
                >
                  Confidence{arrow('confidence')}
                </TableHeaderCell>
                <TableHeaderCell
                  className={mergeClasses(styles.colWait, styles.sortable)}
                  onClick={() => sortBy('waiting')}
                >
                  Waiting{arrow('waiting')}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((demand) => {
                const tier = reviewTier(demand)
                const isCursor = cursor?.id === demand.id
                const routedOnward = demand.disposition === 'digital_council'
                // Done = ticked this sitting, confirmed durably by anyone, OR
                // already routed onward. A routed row has no landing to accept
                // (`confirm-landing` refuses it, and Enter just walks past), so
                // offering "Accept ✓" on it was a primary button that did
                // nothing at all (#1363). What it keeps is the quiet Change….
                const isDone = isHandled(demand)
                const days = daysWaiting(demand.submission_date ?? demand.created_at)
                const conf = demand.suggestion_confidence
                return (
                  <Fragment key={demand.id}>
                    <TableRow
                      className={mergeClasses(
                        styles.row,
                        isCursor ? styles.rowCursor : styles.rowIdle,
                        isDone && !isCursor && styles.rowDone,
                      )}
                      data-testid={`audit-row-${demand.id}`}
                      data-audit-row={demand.id}
                      {...activatable(() => setCursorId(demand.id))}
                    >
                      <TableCell className={styles.cellTop}>
                        {isDone ? (
                          <span className={styles.tick} data-testid={`audit-done-${demand.id}`}>
                            ✓
                          </span>
                        ) : (
                          <span
                            className={mergeClasses(styles.dot, dotFor[tier])}
                            title={reviewTierLabel(demand).name}
                          />
                        )}
                      </TableCell>
                      <TableCell className={styles.cellTop}>
                        <div>
                          <Link
                            to={`/demand/${demand.id}`}
                            className={mergeClasses(styles.title, styles.titleLink)}
                            data-testid={`audit-title-${demand.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {demand.title}
                          </Link>
                        </div>
                        <div className={styles.sub}>
                          {demand.requestor_name ?? 'Unknown requestor'}
                        </div>
                      </TableCell>
                      <TableCell className={styles.cellTop}>
                        {routedOnward ? (
                          <>
                            <span className={mergeClasses(styles.code, styles.codeDc)}>
                              {landingCodeLabel(DIGITAL_COUNCIL_TARGET)}
                            </span>
                            <div className={styles.wasFa}>was {demand.focus_area_code}</div>
                          </>
                        ) : (
                          <span
                            className={mergeClasses(
                              styles.code,
                              demand.focus_area_code === 'UN' && styles.codeUn,
                            )}
                          >
                            {demand.focus_area_code}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className={styles.cellTop}>
                        {demand.suggested_focus_area ? (
                          <span
                            className={mergeClasses(
                              styles.code,
                              tier === 'overridden' && styles.codeOverridden,
                            )}
                          >
                            {landingCodeLabel(demand.suggested_focus_area)}
                          </span>
                        ) : (
                          <span className={styles.none}>no answer</span>
                        )}
                      </TableCell>
                      <TableCell className={styles.cellTop}>
                        {conf == null ? (
                          <span className={styles.none}>—</span>
                        ) : (
                          <div className={styles.conf}>
                            <span className={styles.confNum}>{conf.toFixed(2)}</span>
                            <div className={styles.confTrack} aria-hidden="true">
                              <div
                                className={mergeClasses(
                                  styles.confFill,
                                  conf < 0.5 && styles.confFillWeak,
                                )}
                                style={{ width: `${Math.round(conf * 100)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell
                        className={mergeClasses(
                          styles.cellTop,
                          styles.num,
                          days != null && days >= STALE_AFTER_DAYS && styles.stale,
                        )}
                      >
                        {days == null ? '—' : `${days}d`}
                      </TableCell>
                    </TableRow>

                    {isCursor && (
                      <TableRow data-testid={`audit-open-${demand.id}`}>
                        <TableCell colSpan={6} className={styles.openCell}>
                          <div className={styles.sub}>
                            {reviewTierLabel(demand).name} — {reviewTierLabel(demand).why} ·{' '}
                            {demand.requestor_name ?? 'Unknown submitter'}
                          </div>
                          <h3 className={styles.openTitle}>{demand.title}</h3>
                          <div className={styles.openDesc}>{demand.description}</div>
                          <div
                            className={mergeClasses(
                              styles.verdict,
                              tier === 'overridden' && styles.verdictHot,
                            )}
                          >
                            {demand.suggested_focus_area ? (
                              <>
                                Classifier: <strong>{landingCodeLabel(demand.suggested_focus_area)}</strong>
                                {conf != null ? ` (${conf.toFixed(2)})` : ''}
                                {demand.suggestion_overridden ? (
                                  <>
                                    {' — '}
                                    <strong>the requestor chose otherwise</strong>
                                  </>
                                ) : null}
                              </>
                            ) : (
                              'No suggestion — the classifier gave us nothing.'
                            )}
                          </div>
                          {/* A handled row offers no second go (UAT 2026-09-11):
                              buttons that linger invite re-operating on a decision
                              already made. Accept ✓ absorbed "Looks right" — with
                              the server setting the landing, confirming the landing
                              IS accepting the classifier. */}
                          {isDone ? (
                            <div className={styles.acts} data-testid={`audit-card-done-${demand.id}`}>
                              <span className={styles.sub}>
                                {routedOnward ? 'Routed onward ✓' : 'Accepted ✓'}
                              </span>
                              {/* Handled ≠ locked (owner call, 2026-09-11): the loud
                                  buttons go so nobody re-operates by reflex, but a
                                  quiet escape stays for the second look that catches
                                  a slip. */}
                              <Button
                                appearance="transparent"
                                size="small"
                                data-testid={`audit-change-${demand.id}`}
                                {...restoreFocusTarget}
                                onClick={() => openMove(demand)}
                              >
                                Change…
                              </Button>
                              <Link
                                to={`/demand/${demand.id}`}
                                className={styles.openLink}
                                data-testid={`audit-detail-${demand.id}`}
                              >
                                See details
                              </Link>
                            </div>
                          ) : (
                            <div className={styles.acts}>
                              <Button
                                appearance="primary"
                                data-testid={`audit-pass-${demand.id}`}
                                onClick={passCursor}
                                disabled={confirmLanding.isPending}
                              >
                                {confirmLanding.isPending ? 'Saving…' : 'Accept ✓ — next'}
                              </Button>
                              <Button
                                data-testid={`audit-move-${demand.id}`}
                                {...restoreFocusTarget}
                                onClick={() => openMove(demand)}
                              >
                                Move…
                              </Button>
                              <Link
                                to={`/demand/${demand.id}`}
                                className={styles.openLink}
                                data-testid={`audit-detail-${demand.id}`}
                              >
                                See details
                              </Link>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <RouteDemandDialog
        demand={routing}
        onRouted={(id) => setReviewed((prev) => new Set(prev).add(id))}
        onClose={() => setRouting(null)}
      />
    </div>
  )
}

export default AuditQueue
